import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'success';
type Size = 'sm' | 'md';

const variantClass: Record<Variant, string> = {
  primary:
    'bg-gradient-to-b from-teal-500 to-teal-600 text-white shadow-md shadow-teal-900/15 ring-1 ring-teal-500/20 hover:from-teal-400 hover:to-teal-600 hover:shadow-lg hover:shadow-teal-900/20 focus-visible:ring-teal-500/40 disabled:opacity-50',
  secondary:
    'border border-slate-200/90 bg-white text-slate-700 shadow-sm hover:border-slate-300 hover:bg-slate-50/90 focus-visible:ring-slate-400/25',
  danger:
    'border border-rose-200/90 bg-white text-rose-700 shadow-sm hover:bg-rose-50 focus-visible:ring-rose-400/30',
  ghost: 'text-slate-600 hover:bg-slate-100/80 hover:text-slate-900',
  success:
    'bg-gradient-to-b from-emerald-500 to-emerald-600 text-white shadow-md shadow-emerald-900/15 ring-1 ring-emerald-500/20 hover:from-emerald-400 hover:to-emerald-600 focus-visible:ring-emerald-500/40 disabled:opacity-50',
};

const sizeClass: Record<Size, string> = {
  sm: 'rounded-lg px-2.5 py-1.5 text-xs font-medium',
  md: 'rounded-xl px-4 py-2.5 text-sm font-semibold',
};

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
  children: ReactNode;
};

export function Button({
  variant = 'secondary',
  size = 'md',
  className,
  type = 'button',
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cn(
        'inline-flex touch-manipulation items-center justify-center gap-1.5 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none',
        variantClass[variant],
        sizeClass[size],
        className
      )}
      {...props}
    />
  );
}
